import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, XCircle, MessageCircleQuestion, Loader2, AlertCircle, ShieldCheck, Sparkles, Check, Clock, MapPin, Send, Info } from "lucide-react";
import SEO from "@/components/SEO";
import QuoteStatusTimeline from "@/components/QuoteStatusTimeline";
import PropertyDetailMap from "@/components/PropertyDetailMap";
import { Events } from "@/lib/analytics";
import { api } from "@/lib/api";
import { toast } from "sonner";

const money = (n, c = "\u20aa") => {
  if (n === null || n === undefined || String(n).trim() === "") return null;
  const num = typeof n === "number" ? n : parseFloat(String(n).replace(/[^0-9.]/g, ""));
  return Number.isFinite(num) ? `${c}${num.toLocaleString()}` : null;
};

const parsePrice = (v) => {
  const n = parseFloat(String(v ?? "").replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const STATUS_BADGE = {
  Sent: "text-blue-300 border-blue-500/30 bg-blue-500/10",
  Viewed: "text-amber-300 border-amber-500/30 bg-amber-500/10",
  Accepted: "text-emerald-300 border-emerald-500/30 bg-emerald-500/10",
  Declined: "text-red-300 border-red-500/30 bg-red-500/10",
  Revised: "text-purple-300 border-purple-500/30 bg-purple-500/10",
};

function Wordmark() {
  return (
    <div className="flex items-center justify-center gap-2 select-none">
      <Sparkles className="h-5 w-5 text-gold-soft" />
      <span className="font-display text-lg font-semibold tracking-wide text-white">
        Ascendia <span className="text-gold">Detail</span>
      </span>
    </div>
  );
}

function PackageCard({ pkg, currency, selectable, selected, accepted, onSelect }) {
  const price = money(pkg.price, currency);
  const deposit = money(pkg.deposit, currency);
  const discount = money(pkg.discount, currency);
  return (
    <div
      data-testid="public-package-card"
      onClick={selectable ? onSelect : undefined}
      className={`relative ascendia-card p-6 flex flex-col transition-colors ${
        selectable ? "cursor-pointer" : ""
      } ${
        selected
          ? "border-gold ring-1 ring-gold/50"
          : pkg.recommended
          ? "border-gold/40"
          : "border-[color:var(--border-subtle)]"
      }`}
    >
      {pkg.recommended && (
        <span className="absolute -top-3 left-6 rounded-full bg-gold px-3 py-0.5 text-[11px] font-semibold text-black">
          Recommended
        </span>
      )}
      {selectable && (
        <span
          className={`absolute top-4 right-4 flex h-5 w-5 items-center justify-center rounded-full border ${
            selected ? "bg-gold border-gold text-black" : "border-white/30 text-transparent"
          }`}
        >
          <Check className="h-3.5 w-3.5" />
        </span>
      )}
      <h3 className="font-display text-xl font-semibold text-white pr-8">{pkg.name}</h3>
      {pkg.description && <p className="mt-1 text-sm text-secondary">{pkg.description}</p>}

      <div className="mt-4 flex items-end gap-2">
        {price ? (
          <span className="font-display text-3xl font-semibold text-gold">{price}</span>
        ) : (
          <span className="text-sm text-muted-c">Price on request</span>
        )}
      </div>
      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-c">
        {deposit && <span>Deposit to reserve: <span className="text-white/80">{deposit}</span></span>}
        {discount && <span className="text-emerald-300">Includes {discount} discount</span>}
        {pkg.date && (
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" /> {pkg.date}
          </span>
        )}
      </div>

      {(pkg.scope_included || []).length > 0 && (
        <div className="mt-5">
          <p className="text-xs uppercase tracking-wide text-muted-c mb-2">What's included</p>
          <ul className="space-y-1.5">
            {pkg.scope_included.map((s, i) => (
              <li key={`scope-${s}-${i}`} className="flex items-start gap-2 text-sm text-secondary">
                <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-400" /> {s}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(pkg.addons || []).length > 0 && (
        <div className="mt-4">
          <p className="text-xs uppercase tracking-wide text-muted-c mb-2">Add-ons</p>
          <ul className="space-y-1.5">
            {pkg.addons.map((s, i) => (
              <li key={`addon-${s}-${i}`} className="flex items-start gap-2 text-sm text-secondary">
                <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0 text-gold-soft" /> {s}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(pkg.exclusions || []).length > 0 && (
        <div className="mt-4">
          <p className="text-xs uppercase tracking-wide text-muted-c mb-2">Not included</p>
          <ul className="space-y-1">
            {pkg.exclusions.map((s, i) => (
              <li key={`excl-${s}-${i}`} className="flex items-start gap-2 text-sm text-muted-c">
                <XCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-white/30" /> {s}
              </li>
            ))}
          </ul>
        </div>
      )}

      {pkg.notes && <p className="mt-4 text-xs text-muted-c border-l-2 border-gold/40 pl-2">{pkg.notes}</p>}
      {accepted && (
        <div className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-emerald-500/15 border border-emerald-500/30 px-3 py-2 text-sm text-emerald-300">
          <CheckCircle2 className="h-4 w-4" /> You accepted this option
        </div>
      )}
    </div>
  );
}

export default function PublicQuote() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [state, setState] = useState("loading");
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null); // "decline" | "question"
  const [message, setMessage] = useState("");
  const [applyCredit, setApplyCredit] = useState(true);

  const load = useCallback(() => {
    if (!token) return;
    setState("loading");
    api
      .get(`/quote/${token}`)
      .then(({ data }) => {
        setQuote(data);
        setState("ok");
        const pkgs = data.packages || [];
        const rec = pkgs.find((p) => p.recommended) || pkgs[0];
        setSelected((data.accepted_selection && data.accepted_selection.id) || (rec && rec.id) || null);
      })
      .catch(() => setState("notfound"));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when token changes
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const respond = async (action, extra = {}) => {
    setBusy(true);
    try {
      await api.post(`/quote/${token}/respond`, { action, ...extra });
      if (action === "accept") { toast.success("Quote accepted — thank you!"); Events.quoteAccepted({ status: "Accepted" }); }
      if (action === "decline") toast.success("Response recorded");
      if (action === "question") toast.success("Your question has been sent");
      setDialog(null);
      setMessage("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (state === "loading")
    return (
      <div className="min-h-screen bg-[#0B0B0C] flex items-center justify-center text-muted-c">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading your quote…
      </div>
    );

  if (state === "notfound")
    return (
      <div className="min-h-screen bg-[#0B0B0C] flex items-center justify-center px-4">
        <SEO title="Quote" noindex />
        <div className="text-center">
          <AlertCircle className="h-10 w-10 text-gold-soft mx-auto" />
          <h1 className="mt-4 font-display text-2xl font-semibold text-white">Quote not found</h1>
          <p className="mt-2 text-sm text-muted-c">
            This quote link may have expired. Please contact Ascendia Detail for an updated link.
          </p>
        </div>
      </div>
    );

  const q = quote;
  const client = q.client || {};
  const packages = q.packages || [];
  const currency = q.currency || "\u20aa";
  const isAccepted = q.status === "Accepted";
  const isDeclined = q.status === "Declined";
  const acceptedId = q.accepted_selection && q.accepted_selection.id;
  const multi = q.style === "packages" && packages.length > 1;
  const canRespond = !isAccepted && !isDeclined;

  const availableCredit = Number(q.available_credit || 0);
  const selectedPkg = packages.find((p) => p.id === selected) || packages[0];
  const selectedPrice = parsePrice(selectedPkg?.price);
  const creditToApply = applyCredit && availableCredit > 0 && selectedPrice > 0 ? Math.min(availableCredit, selectedPrice) : 0;
  const netTotal = Math.max(0, selectedPrice - creditToApply);
  const acceptedCredit = Number((q.accepted_selection || {}).credit_applied || 0);

  return (
    <div className="min-h-screen bg-[#0B0B0C] text-white" data-testid="public-quote">
      <SEO title={`Quote ${q.ref}`} noindex />

      {/* Header */}
      <header className="border-b border-[color:var(--border-subtle)]">
        <div className="container-ascendia py-5">
          <Wordmark />
        </div>
      </header>

      <div className="container-ascendia py-10 max-w-4xl">
        {/* Title */}
        <div className="text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-gold-soft">Personalised Quote</p>
          <h1 className="mt-2 font-display text-3xl font-semibold text-white">
            {client.full_name ? `Prepared for ${client.full_name}` : "Your Ascendia Quote"}
          </h1>
          <div className="mt-3 flex items-center justify-center gap-3 text-sm text-muted-c">
            <span className="font-mono text-gold-soft">{q.ref}</span>
            <span
              className={`text-[11px] px-2 py-0.5 rounded-full border ${STATUS_BADGE[q.status] || "border-white/20 text-white/70"}`}
              data-testid="public-quote-status"
            >
              {q.status}
            </span>
          </div>
        </div>

        {/* Status timeline — client-facing progress tracker */}
        <QuoteStatusTimeline status={q.status} />

        {/* Property summary */}
        {(client.service_required || client.address || client.city || client.size_sqm) && (
          <div className="mt-8 ascendia-card p-5 grid grid-cols-2 sm:grid-cols-3 gap-y-3 gap-x-4 text-sm">
            {[
              ["Service", client.service_required],
              ["Property", [client.address, client.city].filter(Boolean).join(", ")],
              ["Size", client.size_sqm && `${client.size_sqm} sqm`],
            ]
              .filter(([, v]) => v)
              .map(([k, v]) => (
                <div key={k}>
                  <p className="text-xs uppercase tracking-wide text-muted-c">{k}</p>
                  <p className="mt-0.5 text-white break-words inline-flex items-start gap-1">
                    {k === "Property" && <MapPin className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gold-soft" />}
                    {v}
                  </p>
                </div>
              ))}
          </div>
        )}

        {/* Ascendia proposal — why + property detail map (premium visual quote) */}
        {q.proposal && (q.proposal.why || (q.proposal.detail_map || []).length > 0) && (
          <div className="mt-8 space-y-6" data-testid="quote-proposal">
            {q.proposal.why && (
              <div className="ascendia-card p-6">
                <p className="text-xs uppercase tracking-[0.16em] text-gold-soft">Why we recommend this</p>
                <p className="mt-2 text-sm text-secondary leading-relaxed">{q.proposal.why}</p>
              </div>
            )}
            {(q.proposal.detail_map || []).length > 0 && (
              <div className="ascendia-card p-6">
                <p className="text-xs uppercase tracking-[0.16em] text-gold-soft mb-4">Your property detail map</p>
                <PropertyDetailMap zones={q.proposal.detail_map} />
                <p className="mt-4 text-xs text-muted-c">This map shows the level of attention each area of your property needs — so you can see exactly where the work goes.</p>
              </div>
            )}
          </div>
        )}

        {/* Accepted banner */}
        {isAccepted && (
          <div
            className="mt-8 ascendia-card p-6 border-emerald-500/40 bg-emerald-500/[0.06] text-center"
            data-testid="public-quote-accepted"
          >
            <CheckCircle2 className="h-9 w-9 text-emerald-400 mx-auto" />
            <h2 className="mt-3 font-display text-xl font-semibold text-white">Thank you — your quote is confirmed</h2>
            <p className="mt-2 text-sm text-secondary">
              We've received your acceptance{q.accepted_selection?.name ? ` of the “${q.accepted_selection.name}” option` : ""}.
              {money(q.accepted_selection?.deposit, currency)
                ? ` To reserve your date, a deposit of ${money(q.accepted_selection.deposit, currency)} applies.`
                : ""}{" "}
              Our team will be in touch shortly with the next steps.
            </p>
            {acceptedCredit > 0 && (
              <p className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-emerald-500/15 border border-emerald-500/30 px-3 py-1.5 text-sm text-emerald-300" data-testid="public-quote-credit-applied">
                <Sparkles className="h-4 w-4" /> {money(acceptedCredit, currency)} Ascendia credit applied
              </p>
            )}
          </div>
        )}
        {isDeclined && (
          <div className="mt-8 ascendia-card p-6 border-red-500/30 text-center">
            <XCircle className="h-8 w-8 text-red-300 mx-auto" />
            <p className="mt-2 text-sm text-secondary">
              You've declined this quote. If this was a mistake or you'd like a revised option, please reach out to us.
            </p>
          </div>
        )}

        {/* Why this price — plain-language explainer so the number feels fair before accepting */}
        {q.price_explainer && (
          <div className="mt-8 ascendia-card p-6" data-testid="quote-why-price">
            <div className="flex items-center gap-2 mb-2">
              <Info className="h-4 w-4 text-gold-soft" />
              <h3 className="font-display font-semibold text-white">Why this price</h3>
            </div>
            <p className="text-sm text-secondary leading-relaxed">{q.price_explainer.intro}</p>
            {(q.price_explainer.factors || []).length > 0 && (
              <ul className="mt-4 space-y-2" data-testid="quote-why-price-factors">
                {q.price_explainer.factors.map((f, i) => (
                  <li key={`why-${i}`} className="flex items-start gap-2 text-sm text-secondary">
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-400" /> {f}
                  </li>
                ))}
              </ul>
            )}
            {q.price_explainer.assurance && (
              <p className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-gold/10 border border-gold/25 px-3 py-1.5 text-xs text-gold-soft">
                <ShieldCheck className="h-3.5 w-3.5" /> {q.price_explainer.assurance}
              </p>
            )}
          </div>
        )}

        {/* Packages */}
        <div className="mt-8">
          {multi && canRespond && (
            <p className="text-center text-sm text-muted-c mb-4">Select the option that suits you best, then accept below.</p>
          )}
          <div className={`grid gap-5 ${multi ? "md:grid-cols-2 lg:grid-cols-3" : "max-w-xl mx-auto"}`}>
            {packages.map((p) => (
              <PackageCard
                key={p.id}
                pkg={p}
                currency={currency}
                selectable={multi && canRespond}
                selected={multi ? selected === p.id : false}
                accepted={isAccepted && acceptedId === p.id}
                onSelect={() => setSelected(p.id)}
              />
            ))}
          </div>
        </div>

        {/* Terms */}
        {q.terms && (
          <div className="mt-8 ascendia-card p-5">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="h-4 w-4 text-gold-soft" />
              <h3 className="font-display font-semibold text-white">Terms</h3>
            </div>
            <p className="text-sm text-secondary whitespace-pre-line">{q.terms}</p>
          </div>
        )}

        {/* Reward credit — apply your Ascendia balance to this quote */}
        {canRespond && availableCredit > 0 && (
          <div className="mt-8 ascendia-card p-6 border-emerald-500/30 bg-emerald-500/[0.05]" data-testid="quote-credit-card">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500/15 border border-emerald-500/30">
                  <Sparkles className="h-4 w-4 text-emerald-300" />
                </span>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.14em] text-emerald-300">Ascendia reward credit</p>
                  <p className="mt-0.5 text-white font-semibold">{money(availableCredit, currency)} available</p>
                  <p className="mt-0.5 text-sm text-muted-c">Apply it to reduce the total on this quote.</p>
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={applyCredit}
                onClick={() => setApplyCredit((v) => !v)}
                data-testid="quote-credit-toggle"
                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${applyCredit ? "bg-emerald-500" : "bg-white/15"}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${applyCredit ? "translate-x-6" : "translate-x-1"}`} />
              </button>
            </div>
            {selectedPrice > 0 && (
              <div className="mt-5 space-y-1.5 border-t border-[color:var(--border-subtle)] pt-4 text-sm" data-testid="quote-credit-summary">
                <div className="flex items-center justify-between text-secondary">
                  <span>{multi ? (selectedPkg?.name || "Selected option") : "Quote total"}</span>
                  <span>{money(selectedPrice, currency)}</span>
                </div>
                {creditToApply > 0 && (
                  <div className="flex items-center justify-between text-emerald-300">
                    <span>Credit applied</span>
                    <span data-testid="quote-credit-applied">-{money(creditToApply, currency)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between pt-1 text-base font-semibold text-white">
                  <span>You pay</span>
                  <span data-testid="quote-net-total" className="font-display text-gold">{money(netTotal, currency)}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        {canRespond && (
          <div className="mt-8 grid sm:grid-cols-3 gap-3" data-testid="public-quote-actions">
            <button
              onClick={() => respond("accept", { package_id: selected, apply_credit: applyCredit })}
              disabled={busy}
              data-testid="public-quote-accept"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-gold py-3 text-sm font-semibold text-black hover:bg-gold-soft disabled:opacity-50 sm:col-span-1"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Accept quote
            </button>
            <button
              onClick={() => { setDialog("question"); setMessage(""); }}
              disabled={busy}
              data-testid="public-quote-question"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-[color:var(--border-subtle)] py-3 text-sm text-white hover:bg-white/5 disabled:opacity-50"
            >
              <MessageCircleQuestion className="h-4 w-4 text-gold-soft" /> Ask a question
            </button>
            <button
              onClick={() => { setDialog("decline"); setMessage(""); }}
              disabled={busy}
              data-testid="public-quote-decline"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-[color:var(--border-subtle)] py-3 text-sm text-white/70 hover:bg-white/5 disabled:opacity-50"
            >
              <XCircle className="h-4 w-4" /> Decline
            </button>
          </div>
        )}

        <p className="mt-10 text-center text-xs text-muted-c">
          Ascendia Detail · Premium property detailing · This quote was prepared exclusively for you.
        </p>
      </div>

      {/* Dialog for decline / question */}
      {dialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" onClick={() => setDialog(null)}>
          <div
            className="w-full max-w-md ascendia-card p-6 bg-[#101011]"
            onClick={(e) => e.stopPropagation()}
            data-testid="public-quote-dialog"
          >
            <h3 className="font-display text-lg font-semibold text-white">
              {dialog === "decline" ? "Decline this quote" : "Ask us a question"}
            </h3>
            <p className="mt-1 text-sm text-muted-c">
              {dialog === "decline"
                ? "Let us know why (optional). We'd love the chance to adjust it for you."
                : "Send your question and our team will get back to you shortly."}
            </p>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              data-testid="public-quote-message"
              placeholder={dialog === "decline" ? "Reason (optional)…" : "Type your question…"}
              className="mt-4 w-full rounded-xl bg-black/40 border border-[color:var(--border-subtle)] px-4 py-3 text-sm text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)]"
            />
            <div className="mt-4 flex gap-3">
              <button
                onClick={() => setDialog(null)}
                className="flex-1 rounded-xl border border-[color:var(--border-subtle)] py-2.5 text-sm text-white/70 hover:bg-white/5"
              >
                Cancel
              </button>
              <button
                onClick={() => respond(dialog === "decline" ? "decline" : "question", { message })}
                disabled={busy || (dialog === "question" && !message.trim())}
                data-testid="public-quote-dialog-submit"
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-gold py-2.5 text-sm font-semibold text-black hover:bg-gold-soft disabled:opacity-50"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {dialog === "decline" ? "Submit" : "Send question"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
